import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdCheckCircle, MdErrorOutline, MdPlayArrow } from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { Spinner } from '../../../Components/Spinner';
import { Appearance, Design } from '../../../Components/designConstants';
import { optimizeItemHighlight, optimizeTitleForCap, TitleSlots } from './ai-optimize-utils';
import {
  ITEM_HIGHLIGHT_CHAR_LIMIT,
  TITLE_CHAR_LIMIT,
  TitleStatus
} from './title-compliance-constants';
import { classifyTitle, formatAiError } from './title-compliance-utils';
import { AuditedProduct } from './scoring-engine-constants';

export interface BatchTitleResult {
  /** Absent when the title pass failed, or when nothing needed rewriting. */
  title?: { title: string; slots: TitleSlots; reasoning: string; removedChars: string[] };
  /** Absent when the highlight pass failed. */
  highlight?: { highlight: string; reasoning: string };
}

const CONCURRENCY = 3;

type RunStatus = 'queued' | 'running' | 'done' | 'error';

interface RunRow {
  product: AuditedProduct;
  status: RunStatus;
  newLength?: number;
  highlightLength?: number;
  /** Title was already within the cap, so it was deliberately left untouched. */
  titleSkipped?: boolean;
  error?: string;
}

interface BatchTitleOptimizeModalProps {
  /** Rows with outstanding work — a missing title rewrite, a missing highlight, or both. */
  candidates: AuditedProduct[];
  /** Push completed work up to the view so it lands in the persisted store. */
  onResult: (productId: string, result: BatchTitleResult) => void;
  onClose: () => void;
}

/**
 * Bulk-runs the title and item-highlight passes over the queue via a
 * capped-concurrency worker pool. Results are written straight into the view's
 * stores (which persist), so the catalog table and drawers fill in live.
 *
 * Previously this ran titles ONLY, which is why testers who used Batch reported
 * that item highlights "did not generate" — nothing ever asked for one.
 *
 * A row is marked failed only if BOTH passes fail. Losing a good title because
 * its highlight errored would be worse than reporting a partial result, so each
 * pass is captured independently and whatever succeeded is still saved.
 */
export const BatchTitleOptimizeModal = ({
  candidates,
  onResult,
  onClose
}: BatchTitleOptimizeModalProps) => {
  const [started, setStarted] = useState(false);
  const [rows, setRows] = useState<RunRow[]>(() =>
    candidates.map(product => ({ product, status: 'queued' }))
  );
  const cancelledRef = useRef(false);

  // Cancel in-flight work if the modal unmounts, so we never setState after unmount.
  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    []
  );

  const completedCount = rows.filter(r => r.status === 'done').length;
  const errorCount = rows.filter(r => r.status === 'error').length;
  const runningCount = rows.filter(r => r.status === 'running').length;
  const totalCount = rows.length;
  const finishedCount = completedCount + errorCount;
  const allDone = started && finishedCount === totalCount;
  const pct = totalCount === 0 ? 0 : Math.round((finishedCount / totalCount) * 100);
  // ~11s per ASIN now that each row runs a title pass AND a highlight pass
  // (previously ~6s for the title alone).
  const etaSeconds = Math.max(5, Math.ceil((candidates.length / CONCURRENCY) * 11));

  const runOne = async (index: number): Promise<void> => {
    if (cancelledRef.current) return;
    const product = candidates[index];
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], status: 'running' };
      return next;
    });
    const wasCompliant = classifyTitle(product.title).status !== TitleStatus.NEEDS_UPDATE;
    const result: BatchTitleResult = {};
    const errors: string[] = [];

    // Falls back to the current title so the highlight still has something
    // coherent to complement if the rewrite fails.
    let proposedTitle = product.title;
    try {
      const res = await optimizeTitleForCap(product);
      proposedTitle = res.title;
      result.title = {
        title: res.title,
        slots: res.slots,
        reasoning: res.reasoning,
        removedChars: res.removedChars
      };
    } catch (err) {
      errors.push(`title: ${formatAiError(err)}`);
    }
    if (cancelledRef.current) return;

    try {
      const hl = await optimizeItemHighlight(product, proposedTitle);
      result.highlight = { highlight: hl.highlight, reasoning: hl.reasoning };
    } catch (err) {
      errors.push(`highlight: ${formatAiError(err)}`);
    }
    if (cancelledRef.current) return;

    // Save whatever succeeded, even on a partial failure.
    if (result.title || result.highlight) {
      onResult(product.id, result);
    }

    setRows(prev => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        // Failed only when nothing at all came back.
        status: result.title || result.highlight ? 'done' : 'error',
        newLength: result.title?.title.length,
        highlightLength: result.highlight?.highlight.length,
        titleSkipped: wasCompliant,
        error: errors.length > 0 ? errors.join(' · ') : undefined
      };
      return next;
    });
  };

  const startBatch = async () => {
    setStarted(true);
    // Capped-concurrency worker pool: each worker pulls the next index off a shared counter.
    let nextIndex = 0;
    const worker = async () => {
      while (!cancelledRef.current) {
        const idx = nextIndex++;
        if (idx >= candidates.length) return;
        await runOne(idx);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, candidates.length) }, worker));
  };

  const handleClose = () => {
    cancelledRef.current = true;
    onClose();
  };

  const empty = candidates.length === 0;

  const footerNote = (): string => {
    if (allDone) {
      const failed = errorCount > 0 ? `, ${errorCount} failed (retry from the table)` : '';
      return `Done — ${completedCount} optimized${failed}.`;
    }
    if (started) return `Running ${runningCount} job${runningCount === 1 ? '' : 's'} in parallel…`;
    if (empty) return 'Nothing outstanding.';
    return 'Nothing happens until you click Start.';
  };

  return (
    <Modal show centered size='lg' onHide={handleClose} className='scorecard-title-rewrite-modal'>
      <Modal.Header closeButton>
        <div>
          <span className='scorecard-modal-eyebrow'>Batch Title Compliance</span>
          <Modal.Title>
            {candidates.length} ASIN{candidates.length === 1 ? '' : 's'} to optimize
          </Modal.Title>
          <span className='scorecard-modal-subtitle'>
            Generates a compliant title and an item highlight for each. Titles already within the
            cap are left as they are. Results save automatically and survive reload — review each in
            the table afterward.
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {empty ? (
          <div className='scorecard-modal-success-banner'>
            <MdCheckCircle />
            <p>Every ASIN already has a title and an item highlight.</p>
          </div>
        ) : (
          <>
            {started ? (
              <div className='scorecard-batch-hud'>
                <div className='scorecard-batch-progress'>
                  <div className='scorecard-batch-progress-head'>
                    <span>
                      Batch progress: {finishedCount} / {totalCount}
                    </span>
                    <span>{pct}%</span>
                  </div>
                  <div className='scorecard-batch-progress-track'>
                    <div className='scorecard-batch-progress-fill' style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className='scorecard-batch-counters'>
                  <div>
                    <span>Done</span>
                    <strong className='scorecard-batch-count-done'>{completedCount}</strong>
                  </div>
                  <div>
                    <span>Total</span>
                    <strong>{totalCount}</strong>
                  </div>
                  {errorCount > 0 && (
                    <div>
                      <span>Failed</span>
                      <strong className='tone-danger'>{errorCount}</strong>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className='scorecard-batch-explainer'>
                <h4>What happens when you start</h4>
                <ul>
                  <li>
                    For each of <strong>{candidates.length}</strong> ASINs the AI writes a compliant
                    title and an item highlight, {CONCURRENCY} at a time.
                  </li>
                  <li>
                    Titles already within {TITLE_CHAR_LIMIT} characters are left untouched — those
                    rows still get a highlight.
                  </li>
                  <li>
                    Every result is hard-validated (≤{TITLE_CHAR_LIMIT} chars for titles, ≤
                    {ITEM_HIGHLIGHT_CHAR_LIMIT} for highlights) and saved automatically.
                  </li>
                  <li>
                    The catalog table and drawers fill in live; nothing is locked — re-run any row
                    with Redo.
                  </li>
                  <li>Estimated runtime: ~{etaSeconds}s. Each call costs a fraction of a cent.</li>
                </ul>
              </div>
            )}

            <div className='scorecard-batch-rows'>
              {rows.map(row => {
                const cls = classifyTitle(row.product.title);
                return (
                  <div key={row.product.id} className='scorecard-batch-row'>
                    <span className='scorecard-batch-row-status'>
                      {row.status === 'queued' && <span className='scorecard-batch-dot' />}
                      {row.status === 'running' && <Spinner size={16} />}
                      {row.status === 'done' && <MdCheckCircle className='tone-success' />}
                      {row.status === 'error' && <MdErrorOutline className='tone-danger' />}
                    </span>
                    <div className='scorecard-batch-row-info'>
                      <div className='scorecard-batch-row-title' title={row.product.title}>
                        {row.product.title || '(no title)'}
                      </div>
                      <div className='scorecard-batch-row-meta'>
                        {row.product.asin} · {row.product.brand || 'Independent'} ·{' '}
                        {row.status === 'done' && row.titleSkipped ? (
                          <span>title {cls.length} chars, already compliant</span>
                        ) : (
                          <>
                            <span className='tone-danger'>{cls.length}</span>
                            {row.status === 'done' && row.newLength !== undefined && (
                              <span className='scorecard-batch-count-done'> → {row.newLength}</span>
                            )}{' '}
                            chars
                          </>
                        )}
                        {row.status === 'done' && row.highlightLength !== undefined && (
                          <span className='scorecard-batch-count-done'>
                            {' '}
                            · highlight {row.highlightLength}/{ITEM_HIGHLIGHT_CHAR_LIMIT}
                          </span>
                        )}
                      </div>
                      {row.error && <div className='scorecard-batch-row-error'>{row.error}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <p className='scorecard-modal-footnote'>{footerNote()}</p>
        {!started && !empty && (
          <Button leadingElement={<MdPlayArrow />} onClick={startBatch}>
            Start Batch Rewrite
          </Button>
        )}
        {(allDone || empty) && (
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
